import { 
  XMarkIcon, 
  UserPlusIcon, 
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import { projectService } from '../../services/projectService';
import { UserSearchResult } from '../../types/review';

// Constants
const SUCCESS_MESSAGE_TIMEOUT = 3000;
const USER_SEARCH_MIN_LENGTH = 2;
const USER_SEARCH_DEBOUNCE_MS = 250;

interface Collaborator {
  username: string;
  email: string;
  display_name?: string;
  is_owner: boolean;
}

interface InviteCollaboratorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  /** Optional project name shown as a subtitle in the modal header. */
  projectTitle?: string;
}

/**
 * Modal for inviting collaborators to a project
 */
export const InviteCollaboratorsModal: React.FC<
  InviteCollaboratorsModalProps
> = ({ isOpen, onClose, projectId, projectTitle }) => {
  const [email, setEmail] = useState('');
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [collaboratorToRemove, setCollaboratorToRemove] = useState<string | null>(null);
  const [showTraceabilityWarning, setShowTraceabilityWarning] = useState(false);
  const [pendingCollaboratorEmail, setPendingCollaboratorEmail] = useState<string | null>(null);
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedSearchUser, setSelectedSearchUser] =
    useState<UserSearchResult | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  /**
   * Load current collaborators
   */
  const loadCollaborators = React.useCallback(async () => {
    setIsLoadingCollaborators(true);
    setError(null);
    
    try {
      const collab = await projectService.getCollaborators(projectId);
      setCollaborators(collab);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load collaborators',
      );
    } finally {
      setIsLoadingCollaborators(false);
    }
  }, [projectId]);

  // Load collaborators when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCollaborators();
    }
  }, [isOpen, loadCollaborators]);

  /**
   * Handle email input change
   */
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setSelectedSearchUser(null);
    setError(null);
    setSuccessMessage(null);
  };

  /**
   * Select a registered Archie user from autocomplete results.
   */
  const handleSelectUser = (searchUser: UserSearchResult) => {
    setEmail(searchUser.email);
    setSelectedSearchUser(searchUser);
    setUserSearchResults([]);
    setIsSearchOpen(false);
    setError(null);
  };

  useEffect(() => {
    const query = email.trim();
    if (
      query.toLowerCase() === selectedSearchUser?.email.toLowerCase()
    ) {
      setUserSearchResults([]);
      setIsSearchOpen(false);
      return undefined;
    }

    if (query.length < USER_SEARCH_MIN_LENGTH) {
      setUserSearchResults([]);
      setIsSearchOpen(false);
      return undefined;
    }

    let isDisposed = false;
    const searchUsers = async (): Promise<void> => {
      setIsSearchingUsers(true);
      try {
        const results = await projectService.searchUsers(query);
        if (!isDisposed) {
          setUserSearchResults(results);
          setIsSearchOpen(results.length > 0);
        }
      } catch {
        if (!isDisposed) {
          setUserSearchResults([]);
          setIsSearchOpen(false);
        }
      } finally {
        if (!isDisposed) {
          setIsSearchingUsers(false);
        }
      }
    };

    const timeoutId = globalThis.setTimeout(searchUsers, USER_SEARCH_DEBOUNCE_MS);
    return () => {
      isDisposed = true;
      globalThis.clearTimeout(timeoutId);
    };
  }, [email, selectedSearchUser]);

  /**
   * Handle form submit - show warning modal before adding collaborator
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation - using atomic grouping simulation to prevent backtracking
    // Pattern: local-part @ domain . tld
    // Uses character class subtraction to prevent catastrophic backtracking
    const emailRegex = new RegExp(
      '^[a-zA-Z0-9.!#$%&\'*+/=?^_`{|}~-]+@' +
      '[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?' +
      '(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$',
    );
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Show warning modal first
    setPendingCollaboratorEmail(email);
    setShowTraceabilityWarning(true);
  };

  /**
   * Confirm adding collaborator after warning acknowledgement
   */
  const confirmAddCollaborator = async () => {
    if (!pendingCollaboratorEmail) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setShowTraceabilityWarning(false);

    try {
      const result = await projectService.shareProject(
        projectId,
        pendingCollaboratorEmail,
      );
      setSuccessMessage(result.message);
      setEmail('');
      setPendingCollaboratorEmail(null);
      
      // Reload collaborators list
      await loadCollaborators();
      
      // Clear success message after timeout
      setTimeout(() => setSuccessMessage(null), SUCCESS_MESSAGE_TIMEOUT);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to add collaborator',
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cancel adding collaborator
   */
  const cancelAddCollaborator = () => {
    setShowTraceabilityWarning(false);
    setPendingCollaboratorEmail(null);
  };

  /**
   * Handle remove collaborator
   */
  const handleRemove = async (username: string) => {
    setCollaboratorToRemove(username);
  };

  /**
   * Confirm removal of collaborator
   */
  const confirmRemove = async () => {
    if (!collaboratorToRemove) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await projectService.removeCollaborator(projectId, collaboratorToRemove);
      
      // Reload collaborators list
      await loadCollaborators();
      setCollaboratorToRemove(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to remove collaborator',
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cancel removal of collaborator
   */
  const cancelRemove = () => {
    setCollaboratorToRemove(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop - click to close */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <dialog 
          open
          aria-labelledby="modal-title"
          className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full z-10 p-0 m-0 border-0"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h3 id="modal-title" className="text-lg font-semibold text-gray-900">
                Invite Collaborators
              </h3>
              {projectTitle && (
                <p className="text-sm text-gray-500 mt-1">{projectTitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Add Collaborator Form */}
            <form onSubmit={handleSubmit} className="mb-6">
              <label 
                htmlFor="email" 
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email Address
              </label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleEmailChange}
                    onFocus={() => userSearchResults.length > 0 && setIsSearchOpen(true)}
                    placeholder="Search registered users by name or email"
                    className="w-full px-3 py-2 border border-gray-300 
                      rounded-md shadow-sm focus:outline-none focus:ring-2 
                      focus:ring-[#336094] focus:border-[#336094]"
                    disabled={isLoading}
                    autoComplete="off"
                  />
                  {isSearchOpen && userSearchResults.length > 0 && (
                    <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {userSearchResults.map((searchUser) => (
                        <button
                          key={searchUser.email}
                          type="button"
                          onMouseDown={() => handleSelectUser(searchUser)}
                          className="block w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                        >
                          <span className="block text-sm font-medium text-gray-900">
                            {searchUser.display_name ?? searchUser.username}
                          </span>
                          <span className="block text-xs text-gray-500">
                            {searchUser.email}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {isSearchingUsers && (
                    <span className="absolute right-3 top-2.5 text-xs text-gray-400">
                      Searching…
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !email.trim()}
                  className="inline-flex items-center px-4 py-2 border 
                    border-transparent rounded-md shadow-sm text-sm 
                    font-medium text-white bg-[#336094] hover:bg-[#6e94c0] 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 
                    focus:ring-[#336094] disabled:opacity-50 
                    disabled:cursor-not-allowed gap-2"
                >
                  <UserPlusIcon className="w-5 h-5" />
                  Add
                </button>
              </div>
            </form>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 
                rounded-md text-sm text-red-700"
              >
                {error}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 
                rounded-md text-sm text-green-700 flex items-center gap-2"
              >
                <CheckCircleIcon className="w-5 h-5" />
                {successMessage}
              </div>
            )}

            {/* Collaborators List */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Current Collaborators ({collaborators.filter(c => !c.is_owner).length})
              </h4>

              {isLoadingCollaborators && (
                <div className="text-center py-8">
                  <div className="loading loading-spinner loading-md 
                    text-primary mx-auto"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Loading collaborators...
                  </p>
                </div>
              )}
              {!isLoadingCollaborators && collaborators.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <UserPlusIcon className="w-12 h-12 mx-auto mb-2 
                    text-gray-300"
                  />
                  <p className="text-sm">No collaborators yet</p>
                </div>
              )}
              {!isLoadingCollaborators && collaborators.length > 0 && (
                <div className="space-y-2">
                  {collaborators.map((collaborator) => (
                    <div
                      key={collaborator.username}
                      className="flex items-center justify-between p-3 
                        bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">
                            {collaborator.display_name ?? collaborator.username}
                          </p>
                          {collaborator.is_owner && (
                            <span className="px-2 py-0.5 text-xs font-medium 
                              bg-[#336094] text-white rounded"
                            >
                              Owner
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {collaborator.email}
                        </p>
                      </div>
                      {!collaborator.is_owner && (
                        <button
                          onClick={() => handleRemove(collaborator.username)}
                          disabled={isLoading}
                          className="text-red-600 hover:text-red-700 
                            disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove collaborator"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md 
                text-sm font-medium text-gray-700 hover:bg-gray-50 
                focus:outline-none focus:ring-2 focus:ring-offset-2 
                focus:ring-[#336094]"
            >
              Close
            </button>
          </div>
        </dialog>
      </div>

      {/* Confirmation Modal for Removing Collaborator */}
      {collaboratorToRemove && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            aria-hidden="true"
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Header */}
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  Remove Collaborator
                </h3>
              </div>

              {/* Body */}
              <div className="p-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to remove{' '}
                  <span className="font-medium">
                    {(() => {
                      const collaborator = collaborators.find(c => c.username === collaboratorToRemove);
                      return collaborator?.display_name ?? collaborator?.username;
                    })()}
                  </span>{' '}
                  from this project?
                </p>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  onClick={cancelRemove}
                  disabled={isLoading}
                  className="px-4 py-2 border border-gray-300 rounded-md 
                    text-sm font-medium text-gray-700 hover:bg-gray-50 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 
                    focus:ring-[#336094] disabled:opacity-50 
                    disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemove}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md 
                    text-sm font-medium hover:bg-red-700 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 
                    focus:ring-red-500 disabled:opacity-50 
                    disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Traceability Warning Modal */}
      {showTraceabilityWarning && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            aria-hidden="true"
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Header with warning icon */}
              <div className="p-6 border-b bg-amber-50">
                <div className="flex items-center gap-3">
                  <ExclamationTriangleIcon className="w-6 h-6 text-amber-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Share Project - Important Notice
                  </h3>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-700">
                  You are about to share this project with a collaborator.
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-sm font-medium text-amber-900 mb-2">
                    ⚠️ Traceability and Blaming Not Available
                  </p>
                  <ul className="text-xs text-amber-800 space-y-1 list-disc pl-4">
                    <li>
                      Changes made by collaborators are not tracked
                    </li>
                    <li>
                      Additions and deletions cannot be attributed to specific users
                    </li>
                    <li>
                      There is no audit trail for collaborative edits
                    </li>
                  </ul>
                </div>

                <p className="text-sm text-gray-700">
                  Please be careful when sharing this project and only invite 
                  trusted collaborators.
                </p>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  onClick={cancelAddCollaborator}
                  disabled={isLoading}
                  className="px-4 py-2 border border-gray-300 rounded-md 
                    text-sm font-medium text-gray-700 hover:bg-gray-50 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 
                    focus:ring-[#336094] disabled:opacity-50 
                    disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAddCollaborator}
                  disabled={isLoading}
                  className="px-4 py-2 bg-[#336094] text-white rounded-md 
                    text-sm font-medium hover:bg-[#6e94c0] 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 
                    focus:ring-[#336094] disabled:opacity-50 
                    disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Adding...' : 'I Understand, Add Collaborator'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
